import React, { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/planner/lib/apiClient";
import { productLabel } from "@/planner/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Loader2, ImageOff, SkipForward, Camera, Upload } from "lucide-react";
import { uploadProductPhoto } from "@/planner/lib/productPhoto";

// Pick a real product photo from Open Food Facts (searched by barcode and name), take or upload your
// own, or paste a link.
// With `queue`, it walks through several products one after another.
export default function PhotoPicker({ open, product, queue, onClose, onSaved }) {
  const [list, setList] = useState([]);
  const [idx, setIdx] = useState(0);
  const current = queue ? list[idx] : product;
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  useEffect(() => { if (open && queue) { setList(queue); setIdx(0); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (text, upc) => {
    setLoading(true); setResults([]);
    try {
      const { data } = await api.get("/products/photo-search", { params: { q: text, upc: upc || "" } });
      setResults(data.results || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo search failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !current) return;
    const text = [current.name, current.variant, current.size].filter(Boolean).join(" ");
    setQ(text); setLink("");
    run(text, current.upc);
  }, [open, current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const next = () => {
    if (queue && idx < list.length - 1) setIdx(idx + 1);
    else onClose();
  };

  const save = async (image, upc) => {
    try {
      const { data } = await api.put(`/products/${current.id}/photo`, { image_path: image, upc: upc || null });
      onSaved && onSaved(data);
      toast.success(image ? `Photo saved for ${data.name}` : `Photo removed from ${data.name}`);
      next();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the photo"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = await uploadProductPhoto(current.id, file);
      onSaved && onSaved(data);
      toast.success(`Photo saved for ${data.name}`);
      next();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Couldn't upload the photo"); }
    finally { setUploading(false); }
  };
  const pick = (camera) => {
    if (camera) fileInput.current.setAttribute("capture", "environment"); else fileInput.current.removeAttribute("capture");
    fileInput.current.click();
  };

  if (!current) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="photo-picker">
        <DialogHeader>
          <DialogTitle>Photo for {productLabel(current)}{current.size ? ` · ${current.size}` : ""}</DialogTitle>
          <DialogDescription>
            {queue ? `${idx + 1} of ${list.length} · ` : ""}Take or upload your own photo, or tap a match from Open Food Facts, a free product database.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => pick(true)} disabled={uploading} data-testid="photo-take"><Camera className="mr-1.5 h-4 w-4" />Take photo</Button>
          <Button type="button" variant="outline" onClick={() => pick(false)} disabled={uploading} data-testid="photo-upload"><Upload className="mr-1.5 h-4 w-4" />Upload</Button>
          {uploading && <span className="flex items-center text-xs text-muted-foreground"><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />Saving photo…</span>}
          <input ref={fileInput} type="file" accept="image/*" className="sr-only" data-testid="photo-file" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        </div>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); run(q, ""); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Brand, product, flavor, size" data-testid="photo-search-input" />
          <Button type="submit" variant="outline"><Search className="mr-1.5 h-4 w-4" />Search</Button>
        </form>
        <div className="grid max-h-[50vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4" data-testid="photo-results">
          {loading && <div className="col-span-full flex items-center justify-center py-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Searching…</div>}
          {!loading && results.length === 0 && <p className="col-span-full py-8 text-center text-sm text-muted-foreground">No photos found. Try fewer words, like just the brand and flavor.</p>}
          {results.map((r) => (
            <button key={`${r.upc}-${r.image}`} type="button" onClick={() => save(r.image, r.upc)}
              className="flex flex-col items-center rounded-md border bg-white p-1.5 text-left hover:ring-2 hover:ring-primary" data-testid="photo-result">
              <img src={r.thumb} alt="" loading="lazy" className="h-24 w-full object-contain" />
              <span className="mt-1 line-clamp-2 w-full text-[11px] font-medium leading-tight text-slate-900">{r.name || "Unnamed"}</span>
              <span className="w-full truncate text-[10px] text-slate-500">{[r.brand, r.size].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t pt-3">
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Or paste a photo link (https://…)" className="h-9 min-w-[200px] flex-1 text-xs" />
          <Button size="sm" variant="outline" disabled={!/^https:\/\//i.test(link)} onClick={() => save(link.trim(), null)}>Use link</Button>
          {fileUrl(current.image_path) && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => save(null, null)}><ImageOff className="mr-1.5 h-4 w-4" />Remove photo</Button>}
          {queue && <Button size="sm" variant="ghost" onClick={next}><SkipForward className="mr-1.5 h-4 w-4" />Skip</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
